import {
  ArrayPath,
  Control,
  FieldArray,
  Path,
  useFieldArray,
} from 'react-hook-form';
import { Label } from '../ui/label';
import StyledButton from '../styled/StyledButton';
import StyledInput from '../styled/StyledInput';
import PlusIcon from '@/assets/icons/plus.svg?react';
import { ExtendedReceiverCompanyFormData } from '@/types/profile/ProfileFormData';
import { useTranslation } from 'react-i18next';

interface ReceiverContactsProps<
  FieldsType extends ExtendedReceiverCompanyFormData,
> {
  control: Control<FieldsType>;
  isViewMode?: boolean;
}

const ReceiverContacts = <FieldsType extends ExtendedReceiverCompanyFormData>({
  control,
  isViewMode = false,
}: ReceiverContactsProps<FieldsType>) => {
  const { t } = useTranslation();
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'company.contacts' as ArrayPath<FieldsType>,
  });

  return (
    <>
      {fields.map((field, index) => (
        <ContactForm
          key={field.id}
          index={index}
          control={control}
          onRemoveContact={() => remove(index)}
          disabled={isViewMode}
        />
      ))}
      {!isViewMode && (
        <StyledButton
          title={t('pages.profile.contacts.addContact')}
          className="text-brand-600 w-fit p-0"
          variant="link"
          icon={<PlusIcon className="w-5 h-5 fill-brand-600" />}
          onClick={() =>
            append({
              name: undefined,
              email: undefined,
              phoneNumber: undefined,
            } as FieldArray<FieldsType, ArrayPath<FieldsType>>[][0])
          }
        />
      )}
    </>
  );
};

export default ReceiverContacts;

const ContactForm = <FieldsType extends ExtendedReceiverCompanyFormData>({
  control,
  index,
  onRemoveContact,
  disabled = false,
}: {
  control: Control<FieldsType>;
  index: number;
  disabled?: boolean;
  onRemoveContact: () => void;
}) => {
  const { t } = useTranslation();

  return (
    <>
      <div className="flex justify-between">
        <Label className="text-gray-700 transition-all duration-200 ease-in-out">
          {t('pages.profile.contacts.contact', { index: index + 1 })}
        </Label>
        {index > 0 && !disabled && (
          <StyledButton
            title={t('pages.profile.contacts.removeContact')}
            className="p-0 h-auto"
            variant="link"
            onClick={onRemoveContact}
          />
        )}
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <StyledInput
          control={control}
          name={`company.contacts.${index}.lastName` as Path<FieldsType>}
          placeholder={t('pages.profile.contacts.name')}
          disabled={disabled}
        />
        <StyledInput
          control={control}
          name={`company.contacts.${index}.firstName` as Path<FieldsType>}
          placeholder={t('pages.profile.contacts.firstName')}
          disabled={disabled}
        />
      </div>
      <div className="flex flex-col md:flex-row gap-4">
        <StyledInput
          control={control}
          name={`company.contacts.${index}.email` as Path<FieldsType>}
          placeholder={t('pages.profile.contacts.email')}
          type="email"
          disabled={disabled}
        />
        <StyledInput
          control={control}
          name={`company.contacts.${index}.phoneNumber` as Path<FieldsType>}
          placeholder={t('pages.profile.contacts.phoneNumber')}
          type="tel"
          disabled={disabled}
        />
      </div>
    </>
  );
};
